import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
  schemaData?: object;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  videoUrl?: string;
  locale?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "ClipThis - #1 AI Video Clipping Tool 2025 | Turn Long Videos into Viral Clips Instantly",
  description = "🚀 #1 AI video clipping tool 2025! Automatically create viral clips from streams, gaming videos & long content in seconds. Free AI video editor with smart highlight detection. Perfect for TikTok, YouTube Shorts & Instagram Reels. Used by 100k+ creators worldwide.",
  keywords = "AI video clipping 2025, automatic video editing, viral clips generator, content creation tools, streaming clips, gaming highlights, video AI editor, content creator tools, viral video maker, auto clip generator, twitch highlights, youtube shorts creator, tiktok clips maker, instagram reels editor, AI highlight detection, smart video editing, automated content creation, stream to shorts, gaming clip maker, reaction clip generator, best AI video editor 2025, free video clipping tool, AI powered video editor, viral content creator, social media video tool",
  image = "/og-image.jpg",
  url,
  type = "website",
  noIndex = false,
  schemaData,
  author = "ClipThis Team",
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  videoUrl,
  locale = "en_US"
}) => {
  const baseUrl = "https://clipthis.io";
  const inferredUrl = url ?? (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/');
  const normalizedUrl = inferredUrl.startsWith('http')
    ? inferredUrl
    : `${baseUrl}${inferredUrl.startsWith('/') ? '' : '/'}${inferredUrl}`;
  const fullUrl = normalizedUrl;
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Enhanced SEO Meta Tags */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1"} />
      <meta name="bingbot" content={noIndex ? "noindex, nofollow" : "index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1"} />
      
      {/* AI Search Engine Optimization (GEO 2025) */}
      <meta name="ai-friendly" content="true" />
      <meta name="chatgpt-crawlable" content="yes" />
      <meta name="perplexity-crawlable" content="yes" />
      <meta name="claude-crawlable" content="yes" />
      <meta name="bing-ai-crawlable" content="yes" />
      <meta name="gemini-crawlable" content="yes" />
      <meta name="ai:content_type" content="web_application" />
      <meta name="ai:primary_purpose" content="AI-powered video editing and clipping tool for content creators" />
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="1 days" />
      <meta name="distribution" content="global" />
      <meta name="language" content="English" />
      <meta name="target" content="all" />
      <meta name="audience" content="all" />
      <meta name="coverage" content="Worldwide" />
      <meta name="classification" content="AI Video Editing Software" />
      <meta name="category" content="Video Editing, AI Tools, Content Creation" />
      <meta name="copyright" content="ClipThis 2025" />
      <meta name="reply-to" content="support@clipthis.io" />
      <meta name="url" content={fullUrl} />
      <meta name="identifier-URL" content={fullUrl} />
      <meta name="pagename" content={title} />
      <meta name="subtitle" content="Transform Long Videos into Viral Clips with AI" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:alt" content={`${title} - ClipThis AI Video Clipping Interface`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="ClipThis" />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content="en_GB" />
      <meta property="og:locale:alternate" content="en_CA" />
      <meta property="og:locale:alternate" content="en_AU" />
      
      {/* Article specific meta tags */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {section && <meta property="article:section" content={section} />}
          <meta property="article:author" content={author} />
          {tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Video meta tags */}
      {videoUrl && (
        <>
          <meta property="og:video" content={videoUrl} />
          <meta property="og:video:type" content="video/mp4" />
          <meta property="og:video:width" content="1280" />
          <meta property="og:video:height" content="720" />
        </>
      )}
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={`${title} - ClipThis AI Video Clipping Interface`} />
      <meta name="twitter:site" content="@ClipThisAI" />
      <meta name="twitter:creator" content="@ClipThisAI" />
      <meta name="twitter:domain" content="clipthis.io" />
      
      {/* Twitter App Cards */}
      <meta name="twitter:app:name:iphone" content="ClipThis" />
      <meta name="twitter:app:name:ipad" content="ClipThis" />
      <meta name="twitter:app:name:googleplay" content="ClipThis" />
      
      {/* Twitter video player */}
      {videoUrl && (
        <>
          <meta name="twitter:player" content={videoUrl} />
          <meta name="twitter:player:width" content="1280" />
          <meta name="twitter:player:height" content="720" />
        </>
      )}
      
      {/* Apple Meta Tags */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="ClipThis AI" />
      
      {/* Microsoft Meta Tags */}
      <meta name="msapplication-TileColor" content="#6366f1" />
      <meta name="msapplication-tap-highlight" content="no" />
      <meta name="theme-color" content="#6366f1" />
      
      {/* Structured Data */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData, null, 2)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead; 